import React, { useState } from 'react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Board from './pages/Board';
import CityMap from './pages/CityMap';
import Explorer from './pages/Explorer';
import MagicLoginPage from './pages/MagicLoginPage';
import SitInFab from './components/SitInFab';
import RecentMeetingPill from './components/RecentMeetingPill';
import AcceptInvitePage from './pages/AcceptInvitePage';
import VerifyFirstLoginPage from './pages/VerifyFirstLoginPage';

interface FirstLoginState {
  preauthToken: string;
  emailHint?: string;
  name?: string;
}

/* ── Route-level error boundary ──────────────────────────────────────
 *  Wraps every routed page so a render-time throw inside a page
 *  component can't unmount the entire React tree and leave the
 *  browser showing pure white. The Explorer route in particular has
 *  a heavy 3D dependency graph (Three.js + R3F + drei + 3d-tiles-
 *  renderer + post-processing) where any single broken import can
 *  blow up the whole page on first render. This is the last line of
 *  defense behind any in-page Suspense / error handling.
 */
interface RouteErrorBoundaryState { error: Error | null; }
class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null };
  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Page render crashed:', error, info);
  }
  render() {
    if (this.state.error) {
      const msg = String(this.state.error?.message || this.state.error);
      return (
        <div style={{
          position: 'fixed', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 16,
          background: '#050E1A',
          color: '#F0E8D8', fontFamily: 'EB Garamond, serif', textAlign: 'center',
          padding: 32,
        }}>
          <div style={{ fontSize: 22, color: '#C9A96E', letterSpacing: '0.04em' }}>
            Page failed to load
          </div>
          <div style={{ fontSize: 14, color: '#7A8CA0', maxWidth: 560 }}>
            Something went wrong rendering this page. Refresh to retry, or
            go back to the dashboard.
          </div>
          <div style={{
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            fontSize: 11, color: '#3E5070', maxWidth: 720,
            wordBreak: 'break-word', marginTop: 8,
          }}>
            {msg}
          </div>
          <a href="/" style={{
            marginTop: 12, padding: '10px 22px',
            background: '#C9A96E', color: '#0a1322',
            textDecoration: 'none', fontSize: 14,
            fontFamily: 'EB Garamond, serif', letterSpacing: '0.04em',
            borderRadius: 2,
          }}>
            Back to Dashboard
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [token, setToken] = useState<string | null>(
    sessionStorage.getItem('coast_token')
  );
  // First-login gauntlet state. When non-null, we render the verification page
  // instead of the regular Login or Dashboard.
  const [firstLogin, setFirstLogin] = useState<FirstLoginState | null>(null);
  // Re-render on browser nav (back/forward, history.pushState dispatch)
  const [, setNavTick] = useState(0);
  React.useEffect(() => {
    const handler = () => setNavTick(n => n + 1);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const handleLogin = (newToken: string, name?: string, role?: string) => {
    sessionStorage.setItem('coast_token', newToken);
    if (name) sessionStorage.setItem('coast_name', name);
    if (role) sessionStorage.setItem('coast_role', role);
    setToken(newToken);
    setFirstLogin(null);
    if (window.location.pathname !== '/') {
      window.history.replaceState({}, '', '/');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('coast_token');
    sessionStorage.removeItem('coast_name');
    sessionStorage.removeItem('coast_role');
    setToken(null);
    setFirstLogin(null);
  };

  // Called by Login.tsx when /token returns a preauth token (verified_at=NULL user).
  const handleFirstLoginRequired = (preauthToken: string, emailHint?: string, name?: string) => {
    setFirstLogin({ preauthToken, emailHint, name });
  };

  // Accept invite landing — unchanged flow to create the account, then back to login.
  if (window.location.pathname.startsWith('/accept-invite/') && !token) {
    return <AcceptInvitePage onLogin={handleLogin} />;
  }

  // Magic link landing
  if (window.location.pathname === '/magic-login' && !token) {
    return <MagicLoginPage onLogin={handleLogin} />;
  }

  // First-login verification takes priority over the normal login screen.
  if (firstLogin && !token) {
    return (
      <VerifyFirstLoginPage
        preauthToken={firstLogin.preauthToken}
        emailHint={firstLogin.emailHint}
        name={firstLogin.name}
        onLogin={handleLogin}
        onCancel={() => setFirstLogin(null)}
      />
    );
  }

  if (!token) {
    return <Login onLogin={handleLogin} onFirstLoginRequired={handleFirstLoginRequired} />;
  }

  // /board is the new Freeform-style canvas; everything else is the legacy
  // dashboard.
  const goBoard = () => {
    window.history.pushState({}, '', '/board');
    window.dispatchEvent(new Event('popstate'));
  };
  const goDashboard = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new Event('popstate'));
  };

  const goMap = () => {
    window.history.pushState({}, '', '/map');
    window.dispatchEvent(new Event('popstate'));
  };

  const goExplorer = () => {
    window.history.pushState({}, '', '/explore');
    window.dispatchEvent(new Event('popstate'));
  };

  // Each authenticated route renders inside a fragment so the global
  // SitInFab can sit alongside the page content without altering the
  // page component itself. The FAB is fixed-positioned, so it doesn't
  // disrupt any page's layout.
  if (window.location.pathname === '/explore') {
    return (
      <>
        <RouteErrorBoundary>
          <Explorer token={token}
            onExitToMap={goMap}
            onExitToDashboard={goDashboard} />
        </RouteErrorBoundary>
        <SitInFab token={token} />
        <RecentMeetingPill />
      </>
    );
  }
  if (window.location.pathname === '/map') {
    return (
      <>
        <RouteErrorBoundary>
          <CityMap token={token}
            onExitToBoard={goBoard}
            onExitToDashboard={goDashboard}
            onOpenExplorer={goExplorer} />
        </RouteErrorBoundary>
        <SitInFab token={token} />
        <RecentMeetingPill />
      </>
    );
  }
  if (window.location.pathname === '/board') {
    return (
      <>
        <RouteErrorBoundary>
          <Board token={token} onExitToDashboard={goDashboard} onOpenMap={goMap} />
        </RouteErrorBoundary>
        <SitInFab token={token} />
        <RecentMeetingPill />
      </>
    );
  }
  return (
    <>
      <RouteErrorBoundary>
        <Dashboard token={token} onLogout={handleLogout}
          onOpenBoard={goBoard} onOpenMap={goMap} />
      </RouteErrorBoundary>
      <SitInFab token={token} />
    </>
  );
}

export default App;
